import { ALERT_TYPES, ANALYTICS_EVENTS } from '@nandosaus/constants';
import { get, isNil, map } from 'lodash';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Alert } from '../alert';
import { analytics } from '../../analytics';
import { Box, Flex } from '../grid';
import { Button } from '../button';
import { CartHeader } from './cart-header';
import { DefaultState } from '../default';
import { getItemLabelWithVariantTypes } from '../../utils/perks-store-data';
import { ItemList } from './item-list';
import { navigation } from '../../constants';
import { OrderTotals } from '../order-totals';
import { useIsMinWidth } from '../../hooks/use-is-min-width';

const PerksShopCart = ({ onCloseCartClick }) => {
  const isWeb = useIsMinWidth('md');
  const { MemberStore, PerksShopStore } = useRootStore();
  const { loading, profile, isSignedIn } = MemberStore;
  const { cartItemsCount, cartItems: perksShopCartItems } = PerksShopStore;
  const router = useRouter();
  const isEmptyCart = isNil(cartItemsCount) || cartItemsCount <= 0;
  const cartValue = PerksShopStore.totalCost;
  const hasSufficientFunds = isSignedIn && get(profile, 'balances.points') >= cartValue;
  const totalCost = `${cartValue} points`;

  const onCheckoutClick = () => {
    PerksShopStore.setIsVisibleCart(false);

    router.push(
      {
        pathname: navigation.PERKS_SHOP_CHECKOUT,
      },
      navigation.PERKS_SHOP_CHECKOUT
    );
  };

  useEffect(() => {
    analytics.track(ANALYTICS_EVENTS.CART_VIEWED, {
      products: map(perksShopCartItems, perkShopCartItem => ({
        product_id: perkShopCartItem.id,
        name: getItemLabelWithVariantTypes(perkShopCartItem.heading, perkShopCartItem.selectedVariant),
        quantity: perkShopCartItem.quantity,
      })),
    });
  }, [perksShopCartItems]);

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
      role="region"
      aria-label="Cart Preview"
      minWidth="320px"
      maxWidth="480px"
      width={{ md: '480px' }}
    >
      <CartHeader onCloseCartClick={onCloseCartClick} />
      {isEmptyCart ? (
        <DefaultState title="You haven’t added anything to your cart!" subtitle="Items you add will appear here" />
      ) : (
        <>
          {!loading && !hasSufficientFunds && (
            <Alert type={ALERT_TYPES.INFO}>
              You need {cartValue - get(profile, 'balances.points')} more points to redeem all items in cart. Please
              remove an item to continue.
            </Alert>
          )}

          <Overflow px={1} flex={1}>
            <ItemList items={perksShopCartItems} />
          </Overflow>
          <Footer flexDirection="column">
            <Box px={1}>
              <OrderTotals deliveryCost="FREE" subtotalPrices={{ formattedPrice: totalCost }} showDisclaimer={false} />
            </Box>

            <Button
              variant="action"
              disabled={isEmptyCart || !hasSufficientFunds}
              rightText={isWeb ? totalCost : undefined}
              onClick={onCheckoutClick}
              aria-label="Continue to checkout"
            >
              Continue to checkout
            </Button>
          </Footer>
        </>
      )}
    </Flex>
  );
};

const Footer = styled(Flex)`
  border-top: 4px solid ${({ theme }) => theme.colors.background};
`;

const Overflow = styled(Flex)`
  overflow-y: auto;
`;

PerksShopCart.propTypes = {
  onCloseCartClick: PropTypes.func.isRequired,
};

const ComposedPerksShopCart = observer(PerksShopCart);
export { ComposedPerksShopCart as PerksShopCart };
